import React from 'react';
import { TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { MY_DAY_STEP_STATUS } from '../../../../common/utils/myDay/driverDaySteps';
import { Label, Text } from '../../../components';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { useTheme } from '../../../theme';

const PhaseSection = ({
	title,
	steps,
	collapsible,
	collapsed,
	onToggleCollapsed,
	children,
}) => {
	const styles = useThemedStyles(createStyles);
	const { theme } = useTheme();

	const countableSteps = (steps || []).filter(step => (
		!step.trainingUnavailable &&
		[MY_DAY_STEP_STATUS.DONE, MY_DAY_STEP_STATUS.TODO, MY_DAY_STEP_STATUS.BLOCKED].includes(step.status)
	));
	const doneCount = countableSteps.filter(step => step.status === MY_DAY_STEP_STATUS.DONE).length;

	const header = (
		<View style={[styles.headerRow, !collapsed && styles.headerRowExpanded]}>
			<Text style={styles.title}>{title}</Text>
			<View style={styles.headerRight}>
				{
					countableSteps.length > 0 && (
						<Label
							label={`${doneCount}/${countableSteps.length} done`}
							green={doneCount === countableSteps.length}
							primary={doneCount !== countableSteps.length}
							style={styles.progressLabel}
						/>
					)
				}
				{
					collapsible && (
						<Ionicons
							name={collapsed ? 'chevron-down' : 'chevron-up'}
							size={16}
							color={theme.text2}
							style={styles.chevron}
						/>
					)
				}
			</View>
		</View>
	);

	const progressPercentage = countableSteps.length > 0
		? Math.round((doneCount / countableSteps.length) * 100)
		: 0;

	return (
		<View style={styles.card}>
			{
				collapsible
					? (
						<TouchableOpacity activeOpacity={0.86} onPress={onToggleCollapsed}>
							{header}
						</TouchableOpacity>
					)
					: header
			}
			{
				countableSteps.length > 0 && (
					<View style={[styles.progressTrack, !collapsed && styles.progressTrackExpanded]}>
						<View
							style={[
								styles.progressFill,
								{ width: `${progressPercentage}%` },
								progressPercentage === 100 && styles.progressFillComplete,
							]}
						/>
					</View>
				)
			}
			{!collapsed && children}
		</View>
	);
};

const createStyles = (theme) => ({
	card: {
		backgroundColor: theme.mainBg1,
		borderRadius: 12,
		padding: 12,
		marginBottom: 10,
		borderWidth: 1,
		borderColor: theme.border3,
	},
	headerRow: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	headerRowExpanded: {
		marginBottom: 8,
	},
	headerRight: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	title: {
		fontSize: 16,
		fontWeight: '700',
		color: theme.text1,
	},
	progressLabel: {
		marginBottom: 0,
	},
	chevron: {
		marginLeft: 6,
	},
	progressTrack: {
		height: 3,
		borderRadius: 2,
		backgroundColor: theme.mainBg3,
		marginTop: 8,
		overflow: 'hidden',
	},
	// The expanded header already carries its own bottom margin.
	progressTrackExpanded: {
		marginTop: 0,
		marginBottom: 10,
	},
	progressFill: {
		height: 3,
		borderRadius: 2,
		backgroundColor: theme.primary1,
	},
	progressFillComplete: {
		backgroundColor: theme.success1,
	},
});

export default PhaseSection;
